import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  Button,
  Confirm,
  Container,
  Segment,
  Table,
  Dimmer,
  Loader,
} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import {
  getBillingHistoryAction,
  openInvoiceFileAction,
  voidInvoiceAction,
  showVoidInvoiceConfirmation,
  closeVoidInvoiceConfirmation,
  openInvoiceVoidLogModal,
  getInvoiceVoidLogsAction,
} from 'state/business/actions';
import { SemanticToastContainer } from 'react-semantic-toasts';
import InvoiceVoidLogModal from './InvoiceVoidLogModal';

const VOID_LOG_PAGE_SIZE = 10;

const mapStateToPropsBillingHistory = ({ business }) => ({
  billingHistory: business.billingHistory,
  loadingOutlets: business.loadingOutlets,
  allBusiness: business.allBusiness,
  voidInvoiceConfirmation: business.voidInvoiceConfirmation,
  voidingInvoice: business.voidingInvoice,
});

function BillingHistory(props) {
  const {
    dispatch,
    billingHistory,
    allBusiness,
    loadingOutlets,
    voidInvoiceConfirmation,
    voidingInvoice,
  } = props;
  const businessId = allBusiness.data[allBusiness.activeIndex].id;
  const billingCycle = allBusiness.data[allBusiness.activeIndex].billing_cycle;

  const [selectedInvoice, setSelectedInvoice] = useState(null);

  useEffect(() => {
    dispatch(getBillingHistoryAction({ businessId }));
  }, [businessId, dispatch]);

  const handleClickShowActivityLog = () => {
    dispatch(openInvoiceVoidLogModal());
    dispatch(
      getInvoiceVoidLogsAction({
        businessId,
        page: 1,
        size: VOID_LOG_PAGE_SIZE,
      })
    );
  };

  const handleClickVoid = history => {
    setSelectedInvoice(history);
    dispatch(showVoidInvoiceConfirmation());
  };

  const handleConfirmVoid = () => {
    dispatch(closeVoidInvoiceConfirmation());
    if (selectedInvoice) {
      dispatch(
        voidInvoiceAction({
          businessId,
          invoiceId: selectedInvoice.id,
          invoiceNumber: selectedInvoice.invoice_number,
        })
      );
    }
  };

  const handleCancelVoid = () => {
    dispatch(closeVoidInvoiceConfirmation());
  };

  return (
    <Container
      style={{ overflowY: 'scroll', maxHeight: '50vh', padding: '1vh' }}
    >
      <SemanticToastContainer />
      <Confirm
        open={voidInvoiceConfirmation}
        confirmButton={{ negative: true, content: 'Confirm' }}
        onConfirm={handleConfirmVoid}
        onCancel={handleCancelVoid}
        header="Confirm Void Invoice"
        content="Are you sure to void this invoice? Any progress cannot be rolled back"
      />
      <InvoiceVoidLogModal />
      <p>Billing Cycle : {billingCycle}</p>
      <Button
        onClick={handleClickShowActivityLog}
        style={{ marginBottom: '1vh' }}
      >
        Show Activity Log
      </Button>
      <Segment style={{ padding: '0px' }}>
        <Dimmer active={loadingOutlets || voidingInvoice}>
          <Loader size="massive">Loading</Loader>
        </Dimmer>
        <Table celled selectable style={{ margin: '0px' }}>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Invoice Date</Table.HeaderCell>
              <Table.HeaderCell>Invoice Number</Table.HeaderCell>
              <Table.HeaderCell>Source</Table.HeaderCell>
              <Table.HeaderCell>Amount</Table.HeaderCell>
              <Table.HeaderCell>Status</Table.HeaderCell>
              <Table.HeaderCell>Action</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {billingHistory.data && billingHistory.data.invoices.length > 0 ? (
              billingHistory.data.invoices.map(history => {
                let keys = '';
                Object.keys(history).forEach(key => {
                  keys += history[key];
                });
                const convertDate = payloadDate => {
                  if (payloadDate === null) return '-';
                  const newPayloadDate = payloadDate.substr(
                    0,
                    payloadDate.indexOf('Z') + 1
                  );
                  const dateTimeFormat = new Intl.DateTimeFormat('id', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  });
                  const date = new Date(newPayloadDate);
                  return dateTimeFormat.format(date);
                };
                const formatCurrency = payloadCurrency => {
                  if (payloadCurrency === null) return '-';
                  const formatter = new Intl.NumberFormat('id', {
                    style: 'currency',
                    currency: 'IDR',
                  });

                  return formatter.format(payloadCurrency);
                };
                return (
                  <Table.Row key={`${keys}`}>
                    <Table.Cell key={`${keys}invoice-date`}>
                      {convertDate(history.invoice_date)}
                    </Table.Cell>
                    <Table.Cell key={`${keys}invoice-number`}>
                      {history.invoice_number}
                    </Table.Cell>
                    <Table.Cell key={`${keys}source`}>
                      {history.source_platform}
                    </Table.Cell>
                    <Table.Cell key={`${keys}amount`}>
                      {formatCurrency(history.total_amount)}
                    </Table.Cell>
                    <Table.Cell
                      style={{
                        color: history.status === 'PAID' ? 'green' : 'red',
                      }}
                      key={`${keys}status`}
                    >
                      {history.status.toUpperCase()}
                    </Table.Cell>
                    <Table.Cell key={`${keys}action`}>
                      <Button
                        basic
                        onClick={() =>
                          dispatch(
                            openInvoiceFileAction({
                              businessId,
                              invoiceId: history.id,
                            })
                          )
                        }
                      >
                        View Invoice
                      </Button>
                      {history.source_platform === 'MOKA_BACKOFFICE' &&
                        history.status === 'UNPAID' && (
                          <Button
                            color="red"
                            onClick={() => handleClickVoid(history)}
                          >
                            Void
                          </Button>
                        )}
                    </Table.Cell>
                  </Table.Row>
                );
              })
            ) : (
              <>
                <Table.Row>
                  <Table.Cell colSpan={6} textAlign="center">
                    No Billing History
                  </Table.Cell>
                </Table.Row>
              </>
            )}
          </Table.Body>
        </Table>
      </Segment>
    </Container>
  );
}

BillingHistory.propTypes = {
  dispatch: PropTypes.func.isRequired,
  billingHistory: PropTypes.object.isRequired,
  loadingOutlets: PropTypes.bool.isRequired,
  allBusiness: PropTypes.object.isRequired,
  voidInvoiceConfirmation: PropTypes.bool.isRequired,
  voidingInvoice: PropTypes.bool.isRequired,
};

export { BillingHistory };

export default connect(mapStateToPropsBillingHistory)(BillingHistory);
